import clsx from "clsx";

import { Icon } from "../Icon";
import type { Icons } from "../Icon/utils";

export type AlertType = "default" | "error" | "loading" | "success";

const iconMap: Record<AlertType, keyof typeof Icons> = {
  default: "fasExclamationTriangle",
  error: "circleExclamation",
  loading: "spinner",
  success: "circleCheck",
};

const colorMap: Record<AlertType, string> = {
  default: "text-gray-800",
  error: "text-red-600",
  loading: "text-gray-800",
  success: "text-green-600",
};

export interface AlertProps {
  id?: string;
  message?: string;
  onClose?: (id: string) => void;
  title: string;
  type?: AlertType;
  visible?: boolean;
}

export function Alert({
  title,
  message,
  type = "default",
  visible,
  id,
  onClose,
}: AlertProps) {
  return (
    <div
      id={id}
      className={`bg-white relative flex w-[65%] md:w-full max-w-sm items-center gap-3 rounded-lg border p-3 shadow-sm transition-all duration-300 ease-in-out z-[60] ${visible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"}`}
      role="alert"
    >
      <Icon
        name={iconMap[type]}
        className={clsx("h-4 md:h-5 w-4 md:w-5 shrink-0", colorMap[type], {
          "animate-spin": type === "loading",
        })}
      />
      <div className="flex-1">
        <span className="w-full flex items-center justify-between">
          <h5 className="font-medium text-xs md:text-sm text-gray-700">
            {title}
          </h5>
          <Icon
            name="xMark"
            className={clsx(
              "cursor-pointer text-gray-400 hover:text-gray-600",
              onClose && id ? "visible" : "invisible",
            )}
            onClick={() => {
              if (onClose && id) {
                onClose(id);
              }
            }}
          />
        </span>
        {message && (
          <p className="text-sm hidden md:block text-gray-400">{message}</p>
        )}
      </div>
    </div>
  );
}
